import React, { Component } from 'react';
import {
    Col, Form,
    FormGroup, Label, Input,
    Button, Alert, FormText
} from 'reactstrap';

import Header from '../components/Header';

class Setup extends Component {
    constructor(props) {
        super(props);

        this.state = {
            username: '',
            password: '',
            confirmPassword: '',
            submitting: false,
            error: '',
            validationErrors: {},
            secretsConfigured: true,
            x3ApiKeyConfigured: false
        };
    }

    componentDidMount() {
        fetch('/api/setup/status')
            .then(r => r.json())
            .then(data => {
                if (!data.setupRequired) {
                    this.props.history.replace('/auth/basic');
                }
            })
            .catch(() => {});

        fetch('/api/setup/secret-status')
            .then(r => r.json())
            .then(data => {
                this.setState({
                    secretsConfigured: data.secretsConfigured,
                    x3ApiKeyConfigured: data.x3ApiKeyConfigured
                });
            })
            .catch(() => {});
    }

    handleChange = event => {
        this.setState({ [event.target.id]: event.target.value, error: '', validationErrors: {} });
    }

    validate() {
        const { username, password, confirmPassword } = this.state;
        const errors = {};

        if (!username || username.trim().length < 3) {
            errors.username = 'Username must be at least 3 characters.';
        }
        if (!password || password.length < 12) {
            errors.password = 'Password must be at least 12 characters.';
        }
        if (password !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match.';
        }

        return errors;
    }

    handleSubmit = async event => {
        event.preventDefault();

        const errors = this.validate();
        if (Object.keys(errors).length > 0) {
            this.setState({ validationErrors: errors });
            return;
        }

        this.setState({ submitting: true, error: '' });

        try {
            const response = await fetch('/api/setup/create-admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: this.state.username,
                    password: this.state.password,
                    confirmPassword: this.state.confirmPassword
                })
            });

            const data = await response.json();

            if (!response.ok) {
                this.setState({ error: data.message || 'Setup failed. Please try again.' });
                return;
            }

            this.props.history.replace('/auth/basic');

        } catch (err) {
            this.setState({ error: 'Please try again.' });
        } finally {
            this.setState({ submitting: false });
        }
    }

    render() {
        const { username, password, confirmPassword, submitting, error, validationErrors, secretsConfigured, x3ApiKeyConfigured } = this.state;
        const isValid = username.trim().length >= 3 && password.length >= 12 && password === confirmPassword;

        return (
            <div className="App">
                <Header visible={true} />
                <content>
                    <h3>First-Time Setup</h3>
                    <br />
                    <div className="row justify-content-center s-login-page">
                        <div className="col-10 col-sm-7 col-md-5 col-lg-4 s-login-card">
                            {!secretsConfigured && (
                                <Alert color="danger">
                                    <strong>Security configuration incomplete.</strong> The required environment secrets
                                    (JWT_SECRET, SESSION_SECRET, CRYPTR_SECRET) are not set. The server should not have
                                    started without them. Please set these values in the <code>.env</code> file and restart
                                    the service before proceeding.
                                </Alert>
                            )}
                            {secretsConfigured && !x3ApiKeyConfigured && (
                                <Alert color="warning">
                                    <strong>Sage X3 integration:</strong> X_INSCRIBE_API_KEY is not configured. If you use the
                                    Sage X3 integration, add a cryptographically random value for X_INSCRIBE_API_KEY to the{' '}
                                    <code>.env</code> file and restart the service, then configure the same value as the{' '}
                                    <code>X_INSCRIBE_API_KEY</code> outbound header in Sage X3.
                                </Alert>
                            )}
                            {secretsConfigured && x3ApiKeyConfigured && (
                                <Alert color="info">
                                    <strong>Application secrets configured.</strong> If you use the Sage X3 integration,
                                    retrieve the <code>X_INSCRIBE_API_KEY</code> value from the <code>.env</code> file on this
                                    server and configure it in Sage X3 as the <code>X_INSCRIBE_API_KEY</code> outbound
                                    header before testing the integration.
                                </Alert>
                            )}
                            <p className="text-muted">
                                Welcome to Fusion Inscribe. Before you can log in, please create an administrator account.
                            </p>
                            <Form className="form" onSubmit={this.handleSubmit}>
                                <Col>
                                    <FormGroup style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                                        <Label><h6>Username</h6></Label>
                                        <Input
                                            id="username"
                                            name="username"
                                            value={username}
                                            onChange={this.handleChange}
                                            className="login-form-control"
                                            invalid={!!validationErrors.username}
                                            autoComplete="username"
                                        />
                                        {validationErrors.username && (
                                            <FormText color="danger">{validationErrors.username}</FormText>
                                        )}
                                        <FormText color="muted">Minimum 3 characters. Will be stored in uppercase.</FormText>
                                    </FormGroup>
                                </Col>
                                <Col><br />
                                    <FormGroup style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                                        <Label><h6>Password</h6></Label>
                                        <Input
                                            type="password"
                                            id="password"
                                            name="password"
                                            value={password}
                                            onChange={this.handleChange}
                                            className="login-form-control"
                                            invalid={!!validationErrors.password}
                                            autoComplete="new-password"
                                        />
                                        {validationErrors.password && (
                                            <FormText color="danger">{validationErrors.password}</FormText>
                                        )}
                                        <FormText color="muted">Minimum 12 characters.</FormText>
                                    </FormGroup>
                                </Col>
                                <Col><br />
                                    <FormGroup style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                                        <Label><h6>Confirm Password</h6></Label>
                                        <Input
                                            type="password"
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            value={confirmPassword}
                                            onChange={this.handleChange}
                                            className="login-form-control"
                                            invalid={!!validationErrors.confirmPassword}
                                            autoComplete="new-password"
                                        />
                                        {validationErrors.confirmPassword && (
                                            <FormText color="danger">{validationErrors.confirmPassword}</FormText>
                                        )}
                                    </FormGroup>
                                </Col>
                                <br />
                                {error && <Alert color="danger">{error}</Alert>}
                                <Button
                                    color="primary"
                                    className="btn btn-primary"
                                    type="submit"
                                    disabled={!isValid || submitting}
                                >
                                    {submitting ? 'Creating account...' : 'Create Administrator Account'}
                                </Button>
                            </Form>
                        </div>
                    </div>
                    <br />
                </content>
            </div>
        );
    }
}

export default Setup;
